package listening

import AttemptDetail
import ReadingQuestion
import ReadingSet
import UserProgress
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.fontFamily
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginRight
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Audio
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Source
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import supabase

@Composable
fun ListeningTask4Screen(
    taskId: Int = 14, // ID для Tarea 4
    onExit: () -> Unit
) {
    // --- STATE ---
    var questions by remember { mutableStateOf<List<ReadingQuestion>>(emptyList()) }
    var currentSet by remember { mutableStateOf<ReadingSet?>(null) }
    var userAnswers by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }
    var isFinished by remember { mutableStateOf(false) }
    val startTime = remember { window.performance.now() }
    val scope = rememberCoroutineScope()

    // --- ЗАГРУЗКА ---
    LaunchedEffect(taskId) {
        isLoading = true
        try {
            val currentUser = supabase.auth.currentUserOrNull()

            val solvedSetIds = if (currentUser != null) {
                supabase.from("user_progress").select {
                    filter { eq("user_id", currentUser.id); eq("task_id", taskId) }
                }.decodeList<UserProgress>().mapNotNull { it.setId }
            } else emptyList()

            val allSets = supabase.from("reading_sets").select {
                filter { eq("task_type", taskId) }
            }.decodeList<ReadingSet>()

            val targetSet = allSets.firstOrNull { it.id !in solvedSetIds }
            currentSet = targetSet

            if (targetSet != null) {
                questions = supabase.from("reading_items").select {
                    filter { eq("set_id", targetSet.id) }
                    order("order_index", Order.ASCENDING)
                }.decodeList<ReadingQuestion>()
            }
        } catch (e: Exception) {
            console.error("Error Tarea 4: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    // --- ФИНИШ ---
    fun finishTask() {
        if (currentSet == null) return
        isFinished = true

        val correctCount = questions.count { userAnswers[it.id] == it.correctOption }
        val duration = ((window.performance.now() - startTime) / 1000).toInt()

        val details = questions.map { q ->
            AttemptDetail(
                question = q.question,
                userAnswer = userAnswers[q.id] ?: "Sin respuesta",
                correctAnswer = q.correctOption,
                isCorrect = userAnswers[q.id] == q.correctOption
            )
        }

        scope.launch {
            DataRepository.saveProgress(taskId, currentSet!!.id, correctCount, questions.size)
            DataRepository.saveAttempt(
                taskId = taskId,
                setTitle = currentSet!!.title,
                score = correctCount,
                total = questions.size,
                durationSeconds = duration,
                details = details
            )
        }
    }

    // --- UI ---
    Div({
        style {
            padding(20.px); maxWidth(900.px); property("margin", "0 auto")
            fontFamily("Roboto, sans-serif")
        }
    }) {
        if (isLoading) {
            H3 { Text("⏳ Cargando...") }
            return@Div
        }
        if (questions.isEmpty()) {
            Div({ style { textAlign("center"); marginTop(50.px) } }) {
                H2 { Text("🎉 Todo completado") }
                P { Text("No hay más tareas disponibles por ahora.") }
                Button({ onClick { onExit() } }) { Text("Volver") }
            }
            return@Div
        }

        val set = currentSet!!

        // Кнопка выхода
        Button({ onClick { onExit() }; style { marginBottom(15.px); cursor("pointer") } }) { Text("⬅ Salir") }

        // Аудио плеер (Sticky)
        set.audioUrl?.let { url ->
            Div({
                style {
                    position(Position.Companion.Sticky); top(10.px)
                    property("z-index", "100")
                    backgroundColor(Color.white); padding(15.px); borderRadius(12.px)
                    property("box-shadow", "0 4px 15px rgba(0,0,0,0.1)"); marginBottom(20.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Companion.Flex); alignItems(AlignItems.Companion.Center); marginBottom(
                        10.px
                    )
                    }
                }) {
                    Span({ style { fontSize(1.5.cssRem); marginRight(10.px) } }) { Text("🎙️") }
                    Div {
                        H4({ style { margin(0.px); color(Color("#2c3e50")) } }) { Text("Entrevista") }
                        Span({ style { fontSize(0.85.cssRem); color(Color("#7f8c8d")) } }) { Text("Escuche atentamente") }
                    }
                }
                Audio({
                    attr("controls", "")
                    style { width(100.percent) }
                }) { Source({ attr("src", url) }) }
            }
        }

        // Описание задания
        Div({
            style {
                backgroundColor(Color("#e8f6f3")); padding(20.px); borderRadius(10.px)
                border(1.px, LineStyle.Companion.Solid, Color("#d1f2eb")); marginBottom(30.px)
            }
        }) {
            H3({ style { marginTop(0.px); color(Color("#16a085")) } }) { Text(set.title) }
            P({ style { lineHeight("1.6em"); color(Color("#34495e")) } }) {
                Text(set.fullText ?: "Escuche la entrevista y responda las preguntas.")
            }
        }

        // Список вопросов
        questions.forEachIndexed { index, q ->
            Div({
                style {
                    backgroundColor(Color.white); padding(25.px); marginBottom(20.px); borderRadius(12.px)
                    property("box-shadow", "0 2px 8px rgba(0,0,0,0.05)")
                }
            }) {
                // Номер и Текст вопроса
                H4({ style { marginTop(0.px); marginBottom(15.px); color(Color("#2c3e50")); fontSize(1.1.cssRem) } }) {
                    Text("${index + 1}. ${q.question}")
                }

                // Варианты A, B, C
                listOf("A" to q.optionA, "B" to q.optionB, "C" to q.optionC).forEach { (letter, text) ->
                    val isSelected = userAnswers[q.id] == letter
                    val isCorrect = q.correctOption == letter

                    val bgColor = when {
                        isFinished && isCorrect -> Color("#d4edda") // Зеленый
                        isFinished && isSelected && !isCorrect -> Color("#f8d7da") // Красный
                        isSelected -> Color("#d1f2eb") // Бирюзовый (выбрано)
                        else -> Color.white
                    }

                    val borderColor = if (isSelected) Color("#1abc9c") else Color("#ecf0f1")

                    Div({
                        onClick { if (!isFinished) userAnswers = userAnswers + (q.id to letter) }
                        style {
                            display(DisplayStyle.Companion.Flex); alignItems(AlignItems.Companion.Center)
                            padding(12.px); marginBottom(10.px); borderRadius(8.px)
                            border(1.px, LineStyle.Companion.Solid, borderColor)
                            backgroundColor(bgColor)
                            cursor(if (isFinished) "default" else "pointer")
                            property("transition", "all 0.2s")
                        }
                    }) {
                        // Кружок с буквой
                        Span({
                            style {
                                minWidth(28.px); height(28.px); borderRadius(50.percent)
                                backgroundColor(
                                    if (isSelected || (isFinished && isCorrect)) Color("#1abc9c") else Color(
                                        "#bdc3c7"
                                    )
                                )
                                color(Color.white); fontWeight("bold")
                                display(DisplayStyle.Companion.Flex); justifyContent(JustifyContent.Companion.Center); alignItems(
                                AlignItems.Companion.Center
                            )
                                marginRight(15.px); fontSize(0.9.cssRem)
                            }
                        }) { Text(letter) }

                        Text(text)
                    }
                }
            }
        }

        // Кнопка завершения
        if (!isFinished) {
            Button({
                style {
                    marginTop(20.px); width(100.percent); padding(15.px)
                    backgroundColor(Color("#16a085")); color(Color.white)
                    border(0.px); borderRadius(8.px); fontSize(1.2.cssRem); cursor("pointer")
                    fontWeight("bold"); property("box-shadow", "0 4px 10px rgba(22, 160, 133, 0.3)")
                }
                onClick { finishTask() }
            }) { Text("Finalizar") }
        } else {
            val score = questions.count { userAnswers[it.id] == it.correctOption }
            Div({
                style {
                    marginTop(20.px); padding(20.px); backgroundColor(Color("#f1c40f")); color(Color.white)
                    borderRadius(10.px); textAlign("center"); fontWeight("bold"); fontSize(1.5.cssRem)
                }
            }) { Text("Resultado: $score / ${questions.size}") }

            Button({
                onClick { onExit() }
                style { marginTop(15.px); padding(10.px); width(100.percent); cursor("pointer") }
            }) { Text("Volver") }
        }
    }
}